// Educational content data for MenoPath
// Comprehensive menopause education covering all aspects

export const educationCategories = [
  {
    id: 'understanding',
    title: 'Understanding Menopause',
    icon: 'BookOpen',
    color: '#5B21B6',
    topics: ['what-is-menopause', 'three-stages', 'hormones-explained']
  },
  {
    id: 'symptoms',
    title: 'Symptoms & Management',
    icon: 'Heartbeat',
    color: '#EC4899',
    topics: ['common-symptoms', 'managing-hot-flashes', 'sleep-menopause', 'brain-fog']
  },
  {
    id: 'health',
    title: 'Health & Wellness',
    icon: 'FirstAid',
    color: '#10B981',
    topics: ['bone-health', 'heart-health', 'nutrition', 'exercise']
  },
  {
    id: 'lifestyle',
    title: 'Lifestyle & Relationships',
    icon: 'Users',
    color: '#F59E0B',
    topics: ['mental-health', 'intimacy', 'menopause-work']
  },
  {
    id: 'treatment',
    title: 'Treatment Options',
    icon: 'Pill',
    color: '#6366F1',
    topics: ['hrt-explained', 'natural-remedies', 'when-see-doctor']
  }
];

export const educationTopics = {
  'what-is-menopause': {
    id: 'what-is-menopause',
    title: 'What is Menopause?',
    subtitle: 'Understanding the natural transition',
    readTime: '5 min read',
    category: 'understanding',
    content: {
      intro: `Menopause is a natural biological process marking the end of a woman's reproductive years. It's officially diagnosed after 12 consecutive months without a menstrual period. While it's a normal part of aging, the physical and emotional symptoms can significantly impact daily life.`,
      sections: [
        {
          title: 'Definition & Timing',
          content: `Menopause typically occurs between ages 45-55, with the average age being 51 in the United States. However, it can happen earlier (premature menopause before 40) or later. The timing is influenced by genetics, lifestyle factors, and medical history.

**Key Facts:**
• Natural menopause is a gradual process, not a sudden event
• The transition period (perimenopause) can last 4-8 years
• Menopause before age 40 affects about 1% of women
• Smoking can cause menopause 1-2 years earlier`
        },
        {
          title: 'Natural vs. Induced Menopause',
          content: `**Natural Menopause** occurs when the ovaries gradually stop producing eggs and hormones. This is the most common type.

**Induced Menopause** can result from:
• Surgical removal of ovaries (oophorectomy)
• Chemotherapy or radiation therapy
• Certain medications
• Primary ovarian insufficiency

Induced menopause often causes more sudden and intense symptoms because hormone levels drop rapidly rather than gradually.`
        },
        {
          title: 'What Happens in Your Body',
          content: `During menopause, your ovaries produce less estrogen and progesterone. These hormones regulate menstruation and affect many other body systems including:

• **Brain**: Mood, memory, temperature regulation
• **Heart**: Cholesterol levels, blood vessel health
• **Bones**: Calcium absorption, bone density
• **Skin**: Collagen production, elasticity
• **Urinary tract**: Tissue health, bladder control

Understanding these changes helps explain the wide range of symptoms women experience.`
        }
      ],
      keyTakeaways: [
        'Menopause is natural, not a disease or disorder',
        'Average age is 51, but ranges from 45-55',
        'The transition happens gradually over several years',
        'Hormone changes affect multiple body systems'
      ]
    }
  },

  'three-stages': {
    id: 'three-stages',
    title: 'The Three Stages of Menopause',
    subtitle: 'Perimenopause, Menopause, and Postmenopause',
    readTime: '7 min read',
    category: 'understanding',
    content: {
      intro: `The menopausal transition isn't a single event but a journey through three distinct stages. Understanding where you are in this process can help you better manage symptoms and make informed health decisions.`,
      sections: [
        {
          title: 'Stage 1: Perimenopause',
          content: `Perimenopause means "around menopause" and is the transitional phase before menopause. It typically begins in your mid-40s but can start earlier.

**Duration:** 4-8 years on average

**What Happens:**
• Ovaries gradually produce less estrogen
• Menstrual cycles become irregular
• Ovulation becomes unpredictable
• Hormone levels fluctuate widely

**Common Signs:**
• Periods may be shorter, longer, heavier, or lighter
• Skipped periods (but pregnancy is still possible!)
• Hot flashes and night sweats begin
• Sleep disturbances
• Mood changes
• Vaginal dryness may start

**Important:** You can still get pregnant during perimenopause. Continue using contraception until you've had no period for 12 months.`
        },
        {
          title: 'Stage 2: Menopause',
          content: `Menopause is technically a single point in time – the moment when you've gone 12 consecutive months without a menstrual period. After this point, you're considered to be in menopause.

**The 12-Month Rule:**
• Any bleeding resets the clock
• Spotting counts as a period
• Once confirmed, you cannot become pregnant naturally

**Hormone Levels:**
• Estrogen drops to about 10% of premenopausal levels
• FSH rises significantly (often >30 mIU/mL)
• Progesterone becomes nearly undetectable

**Average Age:** 51 years

**Confirmation:** Your doctor may test FSH and estradiol levels, though the 12-month rule remains the standard diagnosis.`
        },
        {
          title: 'Stage 3: Postmenopause',
          content: `Postmenopause refers to all the years after menopause. You'll be postmenopausal for the rest of your life.

**Early Postmenopause (First 5 years):**
• Many symptoms continue but often decrease
• Hot flashes may persist
• Vaginal and urinary symptoms may worsen
• Bone loss accelerates

**Late Postmenopause:**
• Most acute symptoms subside
• Long-term health considerations become priority
• Focus shifts to bone health, heart health, cognitive health

**Health Monitoring:**
• Regular bone density scans (DEXA)
• Cardiovascular health screening
• Breast and cervical cancer screening continues
• Attention to vaginal and urinary health`
        }
      ],
      keyTakeaways: [
        'Perimenopause can last 4-8 years with fluctuating hormones',
        'Menopause is confirmed after 12 months without a period',
        'You can still get pregnant during perimenopause',
        'Postmenopause requires focus on long-term health'
      ]
    }
  },

  'hormones-explained': {
    id: 'hormones-explained',
    title: 'Your Hormones Explained',
    subtitle: 'Understanding FSH, LH, Estrogen, Progesterone & AMH',
    readTime: '8 min read',
    category: 'understanding',
    content: {
      intro: `Hormones are chemical messengers that regulate nearly every process in your body. During menopause, several key hormones change dramatically. Understanding these hormones helps you interpret lab results and make sense of your symptoms.`,
      sections: [
        {
          title: 'Estrogen (Estradiol/E2)',
          content: `Estrogen is the primary female sex hormone, with estradiol (E2) being the most potent form during reproductive years.

**Functions:**
• Regulates menstrual cycle
• Maintains bone density
• Protects cardiovascular system
• Keeps skin elastic and hydrated
• Supports brain function and mood
• Maintains vaginal and urinary tract health

**During Menopause:**
• Levels decline from 30-400 pg/mL to below 30 pg/mL
• Decline causes most menopausal symptoms
• Low levels increase risk of osteoporosis and heart disease

**Testing:** Blood test measuring estradiol levels`
        },
        {
          title: 'FSH (Follicle Stimulating Hormone)',
          content: `FSH is produced by the pituitary gland and stimulates the ovaries to produce eggs and estrogen.

**How It Works:**
• Pituitary releases FSH to signal ovaries
• Ovaries respond by producing estrogen
• Estrogen signals pituitary to reduce FSH
• This feedback loop regulates your cycle

**During Menopause:**
• Ovaries become less responsive to FSH
• Pituitary releases MORE FSH trying to stimulate ovaries
• High FSH levels indicate approaching or confirmed menopause

**Levels:**
• Reproductive years: 3-10 mIU/mL
• Perimenopause: 10-25 mIU/mL (fluctuating)
• Menopause: >25-30 mIU/mL consistently

**Note:** FSH fluctuates significantly during perimenopause, so one test isn't definitive.`
        },
        {
          title: 'LH (Luteinizing Hormone)',
          content: `LH works with FSH to regulate the menstrual cycle and triggers ovulation.

**Functions:**
• Triggers ovulation (LH surge)
• Stimulates progesterone production
• Works in tandem with FSH

**During Menopause:**
• LH levels rise (similar to FSH)
• No longer triggers ovulation
• FSH:LH ratio changes

**Levels:**
• Reproductive years: 2-15 mIU/mL
• Menopause: Elevated, often >20 mIU/mL`
        },
        {
          title: 'Progesterone',
          content: `Progesterone is produced after ovulation and prepares the uterus for pregnancy.

**Functions:**
• Prepares uterine lining for implantation
• Maintains early pregnancy
• Balances estrogen effects
• Promotes sleep and calm

**During Menopause:**
• Declines as ovulation stops
• Becomes nearly undetectable
• Loss contributes to sleep issues and mood changes

**Levels:**
• After ovulation: 5-20 ng/mL
• Menopause: <1 ng/mL`
        },
        {
          title: 'AMH (Anti-Müllerian Hormone)',
          content: `AMH is produced by developing follicles in the ovaries and reflects your remaining egg supply (ovarian reserve).

**Why AMH Matters:**
• Best single marker for ovarian reserve
• Remains stable throughout menstrual cycle
• Predicts time to menopause
• Useful for fertility planning

**Levels by Age:**
• 25 years: 3.0-4.0 ng/mL
• 35 years: 1.5-3.0 ng/mL
• 40 years: 0.5-2.0 ng/mL
• 45+ years: <0.5 ng/mL
• Menopause: Undetectable

**Key Insight:** Very low AMH (<0.5 ng/mL) suggests menopause may occur within 4-5 years.`
        }
      ],
      keyTakeaways: [
        'Estrogen decline causes most menopausal symptoms',
        'High FSH indicates ovaries are becoming less responsive',
        'AMH reflects remaining egg supply and predicts timing',
        'Hormone levels fluctuate during perimenopause'
      ]
    }
  },

  'common-symptoms': {
    id: 'common-symptoms',
    title: 'Common Menopause Symptoms',
    subtitle: 'What to expect and when to seek help',
    readTime: '10 min read',
    category: 'symptoms',
    content: {
      intro: `Menopause affects women differently – some breeze through with minimal symptoms while others experience significant disruption. Understanding the range of symptoms helps you recognize what's normal and when to seek medical advice.`,
      sections: [
        {
          title: 'Vasomotor Symptoms',
          content: `**Hot Flashes**
The most iconic menopause symptom, affecting up to 80% of women.
• Sudden feeling of intense heat
• Usually starts in face, neck, or chest
• Lasts 1-5 minutes typically
• May cause sweating, flushing, rapid heartbeat
• Can occur a few times a week to multiple times per hour

**Night Sweats**
Hot flashes that occur during sleep.
• Can drench bedding and sleepwear
• Disrupt sleep quality significantly
• May require changing clothes/sheets
• Often worse in early postmenopause`
        },
        {
          title: 'Sleep Disturbances',
          content: `Sleep problems affect 40-60% of menopausal women.

**Types of Sleep Issues:**
• Difficulty falling asleep
• Waking frequently during night
• Early morning awakening
• Non-restorative sleep

**Causes:**
• Night sweats disrupting sleep
• Hormonal effects on sleep centers
• Increased anxiety or mood changes
• Bladder issues requiring nighttime trips

**Impact:**
Poor sleep affects mood, memory, weight, and overall health.`
        },
        {
          title: 'Mood & Cognitive Changes',
          content: `**Mood Symptoms:**
• Irritability and mood swings
• Increased anxiety
• Feelings of sadness or depression
• Decreased motivation
• Crying spells

**Cognitive Symptoms ("Brain Fog"):**
• Difficulty concentrating
• Memory lapses (especially word-finding)
• Mental fatigue
• Feeling scattered or unfocused

**Important:** While common, severe depression or anxiety should be evaluated by a healthcare provider. These symptoms often improve after the transition.`
        },
        {
          title: 'Urogenital Symptoms',
          content: `**Vaginal Changes:**
• Dryness and decreased lubrication
• Itching or irritation
• Pain during intercourse
• Thinning of vaginal walls

**Urinary Symptoms:**
• Increased frequency
• Urgency (sudden need to urinate)
• Recurrent UTIs
• Stress incontinence (leaking with cough/sneeze)

**Note:** Unlike hot flashes, these symptoms often worsen over time without treatment.`
        },
        {
          title: 'Physical Changes',
          content: `**Body Composition:**
• Weight gain, especially around abdomen
• Loss of muscle mass
• Changes in body shape

**Skin & Hair:**
• Skin becomes thinner and drier
• Decreased collagen (more wrinkles)
• Hair thinning or texture changes
• Increased facial hair

**Joint & Muscle:**
• Joint stiffness and aches
• Muscle tension
• Increased risk of osteoarthritis`
        },
        {
          title: 'Less Common Symptoms',
          content: `Some women experience:
• Heart palpitations
• Headaches or migraines
• Breast tenderness
• Tingling sensations
• Electric shock feelings
• Burning mouth syndrome
• Tinnitus (ringing in ears)
• Digestive changes

If you experience unusual symptoms, discuss with your healthcare provider to rule out other conditions.`
        }
      ],
      keyTakeaways: [
        'Hot flashes affect up to 80% of women',
        'Sleep disturbances are extremely common',
        'Cognitive symptoms ("brain fog") usually improve over time',
        'Vaginal/urinary symptoms may worsen without treatment',
        'Severe mood changes warrant medical evaluation'
      ]
    }
  },

  'managing-hot-flashes': {
    id: 'managing-hot-flashes',
    title: 'Managing Hot Flashes',
    subtitle: 'Triggers, strategies, and when to seek help',
    readTime: '6 min read',
    category: 'symptoms',
    content: {
      intro: `Hot flashes can range from mildly annoying to severely disruptive. While they're a natural part of menopause, you don't have to just "tough it out." Many strategies can help reduce their frequency and intensity.`,
      sections: [
        {
          title: 'Common Triggers',
          content: `Identifying and avoiding your personal triggers can significantly reduce hot flashes.

**Dietary Triggers:**
• Spicy foods
• Hot beverages
• Alcohol (especially red wine)
• Caffeine
• Sugar

**Environmental Triggers:**
• Warm rooms
• Hot weather
• Hot baths/showers
• Heated blankets

**Lifestyle Triggers:**
• Stress and anxiety
• Smoking
• Tight clothing
• Rushing or hurrying

**Tip:** Keep a hot flash diary for 2 weeks to identify your personal triggers.`
        },
        {
          title: 'Cooling Strategies',
          content: `**Immediate Relief:**
• Portable fan or battery-operated mini fan
• Cold water or ice chips
• Cooling towels or wipes
• Step outside for cool air
• Deep, slow breathing

**Environmental:**
• Keep bedroom cool (65-68°F/18-20°C)
• Use breathable, moisture-wicking fabrics
• Layer clothing for easy removal
• Keep ice water nearby at night

**Products:**
• Cooling pillows
• Moisture-wicking sleepwear
• Bed fans
• Cooling mattress pads`
        },
        {
          title: 'Lifestyle Modifications',
          content: `**Exercise:**
• Regular moderate exercise reduces hot flashes
• Avoid exercising close to bedtime
• Yoga and tai chi may be especially helpful

**Stress Management:**
• Meditation and mindfulness
• Deep breathing exercises
• Progressive muscle relaxation
• Cognitive behavioral therapy (CBT)

**Diet:**
• Maintain healthy weight
• Eat regular meals (blood sugar drops can trigger flashes)
• Consider plant estrogens (soy, flaxseed)
• Stay hydrated`
        },
        {
          title: 'Medical Treatments',
          content: `If lifestyle changes aren't enough, discuss these options with your doctor:

**Hormone Therapy:**
• Most effective treatment for hot flashes
• Reduces frequency by 75-90%
• Available as pills, patches, gels, sprays

**Non-Hormonal Medications:**
• Low-dose antidepressants (SSRIs, SNRIs)
• Gabapentin
• Clonidine
• Oxybutynin

**Newer Options:**
• Fezolinetant (NK3 receptor antagonist)
• Approved specifically for hot flashes`
        },
        {
          title: 'When to Seek Help',
          content: `Contact your healthcare provider if hot flashes:
• Severely disrupt your sleep
• Interfere with work or daily activities
• Cause significant distress
• Are accompanied by other concerning symptoms
• Don't respond to lifestyle modifications

**Red Flags:**
• Night sweats with fever or weight loss
• Hot flashes starting suddenly after years without them
• Symptoms that seem different from typical hot flashes`
        }
      ],
      keyTakeaways: [
        'Identify your personal triggers with a diary',
        'Dress in layers and keep your environment cool',
        'Regular exercise and stress management help',
        'Effective medical treatments are available',
        'Seek help if symptoms severely impact quality of life'
      ]
    }
  },

  'sleep-menopause': {
    id: 'sleep-menopause',
    title: 'Sleep & Menopause',
    subtitle: 'Why sleep changes and how to improve it',
    readTime: '7 min read',
    category: 'symptoms',
    content: {
      intro: `Sleep disturbances affect 40-60% of menopausal women. Poor sleep isn't just about feeling tired – it affects mood, memory, weight, immune function, and overall health. Understanding the causes can help you find solutions.`,
      sections: [
        {
          title: 'Why Sleep Changes',
          content: `**Hormonal Factors:**
• Declining estrogen affects sleep regulation
• Lower progesterone reduces its calming effects
• Changes in melatonin production
• Altered circadian rhythms

**Direct Disruptions:**
• Night sweats waking you up
• Need to urinate during night
• Hot flashes disturbing sleep onset

**Secondary Factors:**
• Anxiety and racing thoughts
• Depression
• Joint pain or discomfort
• Sleep apnea (increases after menopause)`
        },
        {
          title: 'Sleep Hygiene Basics',
          content: `**Consistent Schedule:**
• Same bedtime and wake time daily
• Even on weekends (within 1 hour)
• Avoid long daytime naps

**Bedroom Environment:**
• Cool temperature (65-68°F/18-20°C)
• Dark room (blackout curtains)
• Quiet or white noise
• Comfortable, breathable bedding

**Pre-Sleep Routine:**
• Wind down 30-60 minutes before bed
• Avoid screens (blue light)
• Relaxing activities (reading, bath)
• No work or stressful discussions`
        },
        {
          title: 'Managing Night Sweats',
          content: `**Bedding Choices:**
• Moisture-wicking sheets and pillowcases
• Light, breathable blankets (layer them)
• Cooling mattress topper
• Keep spare sleepwear nearby

**Room Setup:**
• Bedroom fan or air conditioning
• Bed fan that blows under covers
• Cooling pillow
• Window slightly open if safe

**Personal Cooling:**
• Light, loose sleepwear
• Avoid heavy pajamas
• Keep cold water by bed
• Cooling towels ready`
        },
        {
          title: 'Lifestyle Factors',
          content: `**Avoid Before Bed:**
• Caffeine (stop by 2pm)
• Alcohol (disrupts sleep quality)
• Large meals
• Spicy foods
• Excessive fluids (to reduce bathroom trips)

**Helpful Habits:**
• Regular exercise (but not close to bedtime)
• Morning sunlight exposure
• Limit screen time in evening
• Relaxation techniques

**Consider:**
• Warm bath 1-2 hours before bed
• Herbal tea (chamomile, valerian)
• Magnesium supplementation
• Light bedtime snack if hungry`
        },
        {
          title: 'When to Seek Help',
          content: `Talk to your doctor if:
• Sleep problems persist despite good sleep hygiene
• You're excessively tired during the day
• You snore loudly or stop breathing during sleep
• Sleep issues significantly affect your functioning

**Medical Options:**
• Hormone therapy can improve sleep
• Low-dose antidepressants
• Sleep study to rule out sleep apnea
• Cognitive behavioral therapy for insomnia (CBT-I)

**Sleep Apnea Note:** Risk increases after menopause due to weight changes and loss of hormonal protection. Symptoms include snoring, gasping, and daytime sleepiness.`
        }
      ],
      keyTakeaways: [
        'Hormonal changes directly affect sleep quality',
        'Keep bedroom cool and dark',
        'Establish consistent sleep schedule',
        'Avoid alcohol and caffeine before bed',
        'Seek help if problems persist or you suspect sleep apnea'
      ]
    }
  },

  'brain-fog': {
    id: 'brain-fog',
    title: 'Brain Fog & Memory',
    subtitle: 'Cognitive changes and coping strategies',
    readTime: '6 min read',
    category: 'symptoms',
    content: {
      intro: `"Where did I put my keys?" "What was I about to say?" If you're experiencing more of these moments, you're not alone. Cognitive changes, often called "brain fog," affect up to 60% of women during menopause.`,
      sections: [
        {
          title: 'What is Menopause Brain Fog?',
          content: `**Common Experiences:**
• Difficulty finding the right word
• Forgetting why you walked into a room
• Trouble concentrating
• Losing track of conversations
• Misplacing items
• Feeling mentally "fuzzy"

**What It's NOT:**
• Not early dementia
• Not permanent brain damage
• Not "just getting old"

**Reassurance:** Research shows cognitive function typically returns to baseline after the menopause transition. The brain adapts to lower hormone levels.`
        },
        {
          title: 'Why It Happens',
          content: `**Estrogen's Role in the Brain:**
• Supports neurotransmitter function
• Promotes blood flow to brain
• Helps regulate sleep (which affects cognition)
• Supports memory formation

**Contributing Factors:**
• Sleep deprivation from night sweats
• Mood changes (depression affects concentration)
• Stress and anxiety
• Trying to juggle too much

**The Science:** Brain imaging shows temporary changes in brain activity during perimenopause, but these normalize in postmenopause.`
        },
        {
          title: 'Coping Strategies',
          content: `**External Memory Aids:**
• Use your phone for reminders
• Keep lists (paper or digital)
• Designate spots for keys, glasses, etc.
• Write things down immediately
• Use calendar apps religiously

**Mental Strategies:**
• Reduce multitasking
• Complete one task before starting another
• Repeat information back when given
• Create mental associations
• Break complex tasks into steps

**Lifestyle Factors:**
• Prioritize sleep
• Regular exercise (boosts brain function)
• Manage stress
• Stay socially engaged`
        },
        {
          title: 'Brain-Boosting Habits',
          content: `**Physical Exercise:**
• 150 minutes moderate activity weekly
• Particularly beneficial for brain health
• Improves blood flow and neuroplasticity

**Mental Exercise:**
• Learn new skills
• Puzzles and brain games
• Reading and learning
• Social interaction

**Nutrition:**
• Mediterranean diet supports brain health
• Omega-3 fatty acids
• Antioxidant-rich foods
• Adequate hydration

**Sleep:**
• Most critical factor for memory
• Prioritize 7-9 hours
• Address sleep problems`
        },
        {
          title: 'When to Be Concerned',
          content: `Most menopause-related cognitive changes are normal. However, see your doctor if you experience:

• Getting lost in familiar places
• Forgetting close friends' or family members' names
• Difficulty performing routine tasks
• Problems with language (not just word-finding)
• Changes in personality or judgment
• Symptoms that progressively worsen

**Important:** These symptoms warrant evaluation, but they're rarely due to menopause alone. Other conditions (thyroid, vitamin deficiencies, depression) can cause similar symptoms and are treatable.`
        }
      ],
      keyTakeaways: [
        'Brain fog is common and usually temporary',
        'Cognitive function typically normalizes after menopause',
        'Sleep, exercise, and stress management help',
        'Use memory aids without shame',
        'Progressive worsening should be evaluated by a doctor'
      ]
    }
  },

  'bone-health': {
    id: 'bone-health',
    title: 'Bone Health & Osteoporosis',
    subtitle: 'Protecting your bones during and after menopause',
    readTime: '8 min read',
    category: 'health',
    content: {
      intro: `Estrogen plays a crucial role in maintaining bone density. As levels decline during menopause, bone loss accelerates. Women can lose up to 20% of their bone density in the 5-7 years after menopause. Understanding this risk empowers you to take protective action.`,
      sections: [
        {
          title: 'Understanding Bone Loss',
          content: `**How Bones Work:**
• Bones constantly remodel (break down and rebuild)
• Estrogen helps maintain this balance
• When estrogen drops, breakdown exceeds rebuilding
• Result: gradual loss of bone density

**The Timeline:**
• Bone loss begins in perimenopause
• Accelerates dramatically around menopause
• Fastest loss in first 5-7 years postmenopause
• Slows but continues after that

**Statistics:**
• 1 in 2 women over 50 will have an osteoporotic fracture
• Hip, spine, and wrist are most commonly affected
• Hip fractures can significantly impact independence`
        },
        {
          title: 'Risk Factors',
          content: `**Non-Modifiable:**
• Age (risk increases with age)
• Female sex
• Family history of osteoporosis
• Small, thin body frame
• Caucasian or Asian ethnicity
• Early menopause (<45)

**Modifiable:**
• Low calcium and vitamin D intake
• Sedentary lifestyle
• Smoking
• Excessive alcohol (>2 drinks/day)
• Certain medications (steroids, etc.)
• Low body weight

**Medical Conditions:**
• Thyroid disorders
• Rheumatoid arthritis
• Celiac disease
• History of eating disorders`
        },
        {
          title: 'Prevention Strategies',
          content: `**Calcium:**
• Goal: 1,200 mg daily for women over 50
• Food sources: dairy, fortified foods, leafy greens, sardines
• Supplements if dietary intake insufficient
• Don't exceed 2,000 mg daily

**Vitamin D:**
• Goal: 600-800 IU daily (some experts recommend more)
• Helps body absorb calcium
• Sources: sunlight, fatty fish, fortified foods
• Many people need supplements

**Exercise:**
• Weight-bearing exercise (walking, hiking, dancing)
• Resistance training (weights, bands)
• Balance exercises to prevent falls
• Aim for 30 minutes most days`
        },
        {
          title: 'Bone Density Testing',
          content: `**DEXA Scan:**
• Gold standard for measuring bone density
• Painless X-ray of hip and spine
• Results given as T-score

**T-Score Interpretation:**
• Above -1: Normal
• -1 to -2.5: Osteopenia (low bone mass)
• Below -2.5: Osteoporosis

**When to Test:**
• All women at age 65+
• Postmenopausal women with risk factors
• Anyone with a fragility fracture
• Follow-up every 1-2 years if at risk

**FRAX Tool:** Your doctor may use this to calculate your 10-year fracture risk.`
        },
        {
          title: 'Treatment Options',
          content: `If you have osteoporosis or high fracture risk:

**Medications:**
• Bisphosphonates (alendronate, risedronate)
• Denosumab
• Hormone therapy
• Romosozumab (for severe cases)
• Teriparatide/abaloparatide

**Lifestyle Continues:**
• Calcium and vitamin D
• Weight-bearing exercise
• Fall prevention
• Avoid smoking and excess alcohol

**Fall Prevention:**
• Remove tripping hazards
• Good lighting
• Handrails on stairs
• Non-slip mats
• Vision correction`
        }
      ],
      keyTakeaways: [
        'Bone loss accelerates dramatically after menopause',
        '1,200 mg calcium + vitamin D daily',
        'Weight-bearing and resistance exercise essential',
        'Get bone density test by age 65 (earlier if risk factors)',
        'Effective treatments available if needed'
      ]
    }
  },

  'heart-health': {
    id: 'heart-health',
    title: 'Heart Health After Menopause',
    subtitle: 'Cardiovascular risks and protection strategies',
    readTime: '7 min read',
    category: 'health',
    content: {
      intro: `Heart disease is the leading cause of death for women – and risk increases significantly after menopause. Estrogen provides cardiovascular protection; when it declines, that protection diminishes. The good news: many risk factors are modifiable.`,
      sections: [
        {
          title: 'Why Risk Increases',
          content: `**Estrogen's Protective Effects:**
• Helps maintain healthy cholesterol levels
• Keeps blood vessels flexible
• Reduces inflammation
• Improves blood sugar regulation

**After Menopause:**
• LDL ("bad") cholesterol rises
• HDL ("good") cholesterol may drop
• Blood vessels become stiffer
• Blood pressure often increases
• Weight tends to accumulate around abdomen
• Insulin resistance may develop

**Statistics:**
• Heart disease risk equals men's by age 65
• 1 in 3 women will die from cardiovascular disease
• Heart attacks present differently in women`
        },
        {
          title: 'Know Your Numbers',
          content: `Track these key metrics:

**Blood Pressure:**
• Optimal: <120/80 mmHg
• Elevated: 120-129/<80
• High: 130/80 or above

**Cholesterol:**
• Total: <200 mg/dL ideal
• LDL: <100 mg/dL (lower if high risk)
• HDL: >50 mg/dL for women
• Triglycerides: <150 mg/dL

**Blood Sugar:**
• Fasting glucose: <100 mg/dL
• HbA1c: <5.7%

**Body Metrics:**
• BMI: 18.5-24.9
• Waist circumference: <35 inches for women`
        },
        {
          title: 'Heart-Healthy Lifestyle',
          content: `**Nutrition:**
• Mediterranean or DASH diet
• Plenty of fruits and vegetables
• Whole grains over refined
• Lean proteins, fish twice weekly
• Healthy fats (olive oil, nuts, avocado)
• Limit sodium, sugar, saturated fat

**Exercise:**
• 150 minutes moderate aerobic activity weekly
• Or 75 minutes vigorous activity
• Plus strength training 2x/week
• Even small amounts help

**Weight Management:**
• Maintain healthy weight
• Focus on waist circumference
• Abdominal fat is most dangerous`
        },
        {
          title: 'Other Risk Factors',
          content: `**Stop Smoking:**
• Single most important action
• Risk drops quickly after quitting
• Resources available to help quit

**Limit Alcohol:**
• No more than 1 drink daily for women
• Red wine isn't "heart healthy" enough to justify drinking

**Manage Stress:**
• Chronic stress damages heart
• Find healthy coping mechanisms
• Consider meditation, yoga, counseling

**Sleep:**
• 7-9 hours nightly
• Treat sleep apnea if present
• Address insomnia`
        },
        {
          title: 'Women-Specific Concerns',
          content: `**Heart Attack Symptoms in Women:**
May be different from men's classic symptoms:
• Unusual fatigue
• Shortness of breath
• Nausea/vomiting
• Back or jaw pain
• Lightheadedness
• Chest discomfort (may be milder or absent)

**Don't Ignore Symptoms!**
Women often delay seeking help, which can be deadly.

**Hormone Therapy & Heart:**
• Started near menopause may offer some protection
• Should not be used solely for heart protection
• Discuss individual risks with doctor`
        }
      ],
      keyTakeaways: [
        'Estrogen decline increases cardiovascular risk',
        'Know your numbers: BP, cholesterol, blood sugar',
        'Mediterranean diet and regular exercise are protective',
        'Quitting smoking is the most important single action',
        'Heart attack symptoms may differ in women'
      ]
    }
  },

  'nutrition': {
    id: 'nutrition',
    title: 'Nutrition for Menopause',
    subtitle: 'Foods to embrace, foods to limit, and supplements',
    readTime: '8 min read',
    category: 'health',
    content: {
      intro: `What you eat becomes increasingly important during and after menopause. Good nutrition can help manage symptoms, protect your bones and heart, maintain healthy weight, and boost energy. Here's your guide to eating well through the transition.`,
      sections: [
        {
          title: 'Foods to Embrace',
          content: `**Calcium-Rich Foods:**
• Dairy products (milk, yogurt, cheese)
• Fortified non-dairy milk
• Sardines and canned salmon (with bones)
• Leafy greens (kale, collards, broccoli)
• Fortified orange juice

**Protein Sources:**
• Lean poultry and fish
• Legumes (beans, lentils)
• Eggs
• Nuts and seeds
• Tofu and tempeh

**Healthy Fats:**
• Olive oil
• Avocados
• Fatty fish (salmon, mackerel)
• Walnuts and flaxseeds
• Chia seeds`
        },
        {
          title: 'Phytoestrogens',
          content: `Plant compounds that weakly mimic estrogen:

**Sources:**
• Soy products (tofu, edamame, soy milk)
• Flaxseeds (ground)
• Sesame seeds
• Legumes
• Some whole grains

**Benefits:**
• May reduce hot flashes modestly
• Support bone health
• Provide protein and fiber

**Note:** Effects are mild compared to HRT. Most beneficial if eaten regularly as part of diet rather than as supplements. Research is mixed on supplement forms.`
        },
        {
          title: 'Foods to Limit',
          content: `**For Hot Flash Management:**
• Spicy foods (common trigger)
• Caffeine
• Alcohol
• Hot beverages

**For Overall Health:**
• Added sugars and sweets
• Highly processed foods
• Excessive sodium
• Saturated fats (fatty meats, full-fat dairy)
• Trans fats

**For Weight Management:**
• Large portions
• Liquid calories (sugary drinks)
• Late-night eating
• Mindless snacking`
        },
        {
          title: 'Key Nutrients & Supplements',
          content: `**Calcium:**
• Need: 1,200 mg daily (food + supplements)
• Take in divided doses (500 mg max at once)
• Calcium citrate absorbs best

**Vitamin D:**
• Need: 600-800+ IU daily
• Many women need 1,000-2,000 IU
• Get levels tested

**Vitamin B12:**
• Absorption decreases with age
• Important for energy and cognition
• Found in animal products or supplements

**Omega-3 Fatty Acids:**
• Support heart and brain health
• Fatty fish 2x/week or supplement
• Look for EPA and DHA

**Magnesium:**
• Supports sleep and bone health
• Many women are deficient
• Found in nuts, seeds, whole grains`
        },
        {
          title: 'Weight Management',
          content: `Metabolism slows and body composition changes during menopause:

**Challenges:**
• Decreased muscle mass
• Lower calorie needs
• Hormonal changes favoring fat storage
• Shift to abdominal fat

**Strategies:**
• Slightly reduce calories (about 200 fewer/day)
• Protein with every meal (preserves muscle)
• Fiber-rich foods (promote fullness)
• Regular strength training
• Stay active throughout day

**Mindset:**
• Focus on health, not just numbers
• Sustainable habits over quick fixes
• Body composition matters more than weight alone`
        }
      ],
      keyTakeaways: [
        'Calcium and vitamin D are essential',
        'Embrace Mediterranean-style eating',
        'Limit triggers for hot flashes (caffeine, alcohol, spicy foods)',
        'Phytoestrogens may help modestly',
        'Adjust calories slightly and prioritize protein'
      ]
    }
  },

  'exercise': {
    id: 'exercise',
    title: 'Exercise & Fitness During Menopause',
    subtitle: 'Best exercises and benefits of strength training',
    readTime: '7 min read',
    category: 'health',
    content: {
      intro: `Exercise is one of the most powerful tools for managing menopause. It helps with weight, mood, sleep, hot flashes, bone health, and cardiovascular protection. The key is finding activities you enjoy and can sustain.`,
      sections: [
        {
          title: 'Benefits of Exercise',
          content: `**For Menopause Symptoms:**
• Reduces hot flash frequency and intensity
• Improves sleep quality
• Boosts mood and reduces anxiety
• Increases energy levels
• Helps maintain cognitive function

**For Long-Term Health:**
• Protects bone density
• Supports heart health
• Helps maintain healthy weight
• Preserves muscle mass
• Reduces diabetes risk

**Research Shows:**
Women who exercise regularly report better quality of life during menopause.`
        },
        {
          title: 'Types of Exercise You Need',
          content: `**1. Aerobic/Cardio (150 min/week):**
• Walking, hiking
• Swimming, water aerobics
• Cycling
• Dancing
• Tennis, pickleball

**2. Strength Training (2x/week):**
• Weight lifting
• Resistance bands
• Body weight exercises
• Weight machines
• Functional fitness

**3. Flexibility & Balance:**
• Yoga
• Pilates
• Tai chi
• Stretching
• Balance exercises

**Ideal Week:**
• 150 min moderate cardio OR 75 min vigorous
• 2 strength sessions
• Daily stretching/balance work`
        },
        {
          title: 'Why Strength Training Matters',
          content: `After menopause, women lose muscle mass faster. Strength training counteracts this:

**Benefits:**
• Builds and maintains muscle
• Boosts metabolism (muscle burns more calories)
• Strengthens bones
• Improves balance and prevents falls
• Enhances functional ability
• Combats the "middle-age spread"

**Getting Started:**
• Start with body weight or light weights
• Learn proper form (consider a trainer)
• Progress gradually
• Rest 48 hours between sessions for same muscle groups

**No Gym? No Problem:**
• Resistance bands
• Water bottles as weights
• Bodyweight exercises (squats, pushups, planks)
• Online videos and apps`
        },
        {
          title: 'Special Considerations',
          content: `**Hot Flashes:**
• Exercise may trigger them short-term
• Long-term regular exercise reduces them
• Exercise in cool environments
• Stay hydrated

**Joint Issues:**
• Low-impact options (swimming, cycling)
• Warm up thoroughly
• Listen to your body
• Consider physical therapy if needed

**Starting After Being Sedentary:**
• Start slow (10-minute walks)
• Gradually increase
• Any movement is better than none
• Walking is excellent exercise

**Bone Health Concerns:**
• Impact exercise builds bone (walking, dancing)
• Avoid high-risk activities if osteoporosis
• Focus on balance to prevent falls`
        },
        {
          title: 'Making It Stick',
          content: `**Find Your Motivation:**
• Focus on how you feel, not just looks
• Set realistic, specific goals
• Track your progress
• Celebrate small wins

**Make It Enjoyable:**
• Choose activities you actually like
• Exercise with friends
• Try new things
• Mix it up to prevent boredom

**Remove Barriers:**
• Schedule it like an appointment
• Lay out workout clothes
• Keep equipment accessible
• Have a backup plan (indoor options)

**Be Kind to Yourself:**
• Progress, not perfection
• Rest when needed
• Start again after breaks without guilt`
        }
      ],
      keyTakeaways: [
        '150 minutes cardio + 2 strength sessions weekly',
        'Strength training is essential for metabolism and bones',
        'Exercise reduces hot flashes over time',
        'Any movement is better than none',
        'Find activities you enjoy for long-term success'
      ]
    }
  },

  'mental-health': {
    id: 'mental-health',
    title: 'Mental Health & Mood',
    subtitle: 'Anxiety, depression, and emotional wellbeing',
    readTime: '8 min read',
    category: 'lifestyle',
    content: {
      intro: `Menopause isn't just physical – it can significantly impact mental health. Mood swings, anxiety, and depression are common during this transition. Understanding the connection between hormones and mood can help you navigate these changes and know when to seek help.`,
      sections: [
        {
          title: 'The Hormone-Mood Connection',
          content: `**How Hormones Affect Mood:**
• Estrogen influences serotonin (the "feel-good" neurotransmitter)
• Progesterone has calming effects
• Fluctuating hormones cause mood instability
• Sleep disruption worsens mood

**Common Emotional Experiences:**
• Mood swings and irritability
• Increased anxiety or worry
• Feelings of sadness
• Crying more easily
• Feeling overwhelmed
• Loss of confidence
• Difficulty coping with stress

**Important:** You're not imagining it or "being dramatic." These are real biological effects.`
        },
        {
          title: 'Anxiety During Menopause',
          content: `Anxiety is extremely common during perimenopause:

**Symptoms May Include:**
• Persistent worry
• Racing thoughts
• Heart palpitations
• Feeling on edge
• Panic attacks
• Social anxiety
• Health anxiety

**Contributing Factors:**
• Hormone fluctuations
• Sleep deprivation
• Life stressors (aging parents, children leaving)
• Hot flashes can trigger panic-like symptoms
• Uncertainty about body changes

**What Helps:**
• Regular exercise (very effective for anxiety)
• Relaxation techniques
• Limiting caffeine and alcohol
• Adequate sleep
• Cognitive behavioral therapy (CBT)
• Sometimes medication`
        },
        {
          title: 'Depression Considerations',
          content: `Risk of depression increases during perimenopause:

**Warning Signs:**
• Persistent sadness lasting 2+ weeks
• Loss of interest in usual activities
• Significant changes in sleep or appetite
• Fatigue and low energy
• Difficulty concentrating
• Feelings of worthlessness
• Thoughts of death or suicide

**Risk Factors:**
• History of depression
• Previous postpartum depression
• PMS/PMDD history
• Major life stressors
• Lack of social support

**Important:** Depression is treatable. It's not a character flaw or something to "push through." Seek help if you recognize these signs.`
        },
        {
          title: 'Coping Strategies',
          content: `**Self-Care Basics:**
• Prioritize sleep
• Regular exercise
• Healthy eating
• Limit alcohol
• Stay connected socially

**Stress Management:**
• Meditation and mindfulness
• Deep breathing exercises
• Yoga or tai chi
• Journaling
• Time in nature

**Cognitive Strategies:**
• Challenge negative thoughts
• Practice self-compassion
• Set realistic expectations
• Focus on what you can control
• Celebrate small accomplishments

**Social Support:**
• Talk to trusted friends
• Join a menopause support group
• Don't isolate yourself
• Let others help`
        },
        {
          title: 'When to Seek Professional Help',
          content: `**See Your Doctor If:**
• Symptoms significantly affect daily life
• You're unable to work or function normally
• Relationships are suffering
• You're using alcohol or substances to cope
• You have thoughts of self-harm or suicide

**Treatment Options:**
• Talk therapy (CBT is very effective)
• Hormone therapy (can help mood symptoms)
• Antidepressants/anti-anxiety medications
• Combination approaches

**Finding Help:**
• Start with your primary care doctor
• Ask for referral to mental health professional
• Check if your insurance covers therapy
• Online therapy options available

**Crisis Resources:**
If you're in crisis, call 988 (Suicide & Crisis Lifeline) or go to your nearest emergency room.`
        }
      ],
      keyTakeaways: [
        'Mood changes during menopause are real and biological',
        'Exercise is one of the most effective mood boosters',
        'Persistent depression symptoms need professional attention',
        'Multiple effective treatments are available',
        'Seeking help is a sign of strength, not weakness'
      ]
    }
  },

  'intimacy': {
    id: 'intimacy',
    title: 'Intimacy & Relationships',
    subtitle: 'Vaginal health, libido changes, and communication',
    readTime: '7 min read',
    category: 'lifestyle',
    content: {
      intro: `Menopause can significantly impact intimacy, but it doesn't mean the end of a satisfying sex life. Understanding the changes and knowing your options can help you maintain closeness with your partner.`,
      sections: [
        {
          title: 'Physical Changes',
          content: `**Vaginal Changes (Genitourinary Syndrome of Menopause):**
• Decreased lubrication
• Vaginal dryness
• Thinning of vaginal walls
• Decreased elasticity
• Changes in pH (more susceptible to infections)

**Impact on Intimacy:**
• Discomfort or pain during intercourse
• Longer time to become aroused
• Changes in sensation
• Possible bleeding after sex

**Important:** Unlike hot flashes, these symptoms often worsen over time without treatment. Effective solutions exist.`
        },
        {
          title: 'Libido Changes',
          content: `Desire may change during menopause:

**Factors Affecting Desire:**
• Hormonal changes (testosterone also declines)
• Vaginal discomfort
• Fatigue and sleep issues
• Body image concerns
• Relationship dynamics
• Stress and mood changes
• Medications

**What's Normal:**
• Some women experience decreased desire
• Some notice no change
• Some feel freed and more interested
• Fluctuations are common

**Remember:** Lower frequency doesn't mean less intimacy. Quality matters more than quantity.`
        },
        {
          title: 'Solutions for Vaginal Symptoms',
          content: `**Over-the-Counter:**
• Vaginal moisturizers (use regularly, not just for sex)
• Water-based lubricants (use during intimacy)
• Avoid irritants (scented products, douches)

**Prescription Options:**
• Low-dose vaginal estrogen (very effective, minimal absorption)
  - Creams
  - Tablets
  - Rings
• DHEA vaginal insert (Intrarosa)
• Ospemifene (oral medication)

**Other Approaches:**
• Regular sexual activity maintains tissue health
• Vaginal dilators if needed
• Pelvic floor physical therapy

**Talk to Your Doctor:** Don't suffer in silence. These are common issues with good solutions.`
        },
        {
          title: 'Maintaining Intimacy',
          content: `**Communication:**
• Talk openly with your partner
• Express your needs and concerns
• Discuss what feels good now
• Be patient with each other

**Adapt and Explore:**
• Try different positions
• Focus on foreplay and arousal
• Expand definition of intimacy
• Use lubricants generously
• Schedule intimate time

**Relationship Health:**
• Maintain emotional connection
• Continue dating each other
• Physical affection beyond sex
• Address relationship issues

**Self-Image:**
• Your body is still worthy of pleasure
• Challenge negative self-talk
• Focus on sensation, not appearance`
        },
        {
          title: 'When to Seek Help',
          content: `**Talk to a Healthcare Provider If:**
• Pain during intercourse persists
• Vaginal symptoms significantly impact quality of life
• Over-the-counter solutions aren't helping
• Bleeding occurs after sex
• You have concerns about treatment options

**Consider a Therapist If:**
• Communication with partner is difficult
• Body image issues are significant
• Past trauma is affecting intimacy
• Relationship problems are present

**Resources:**
• Menopause specialist or gynecologist
• Sex therapist (AASECT certified)
• Pelvic floor physical therapist
• Couples counseling`
        }
      ],
      keyTakeaways: [
        'Vaginal symptoms are treatable - don\'t suffer in silence',
        'Lubricants and moisturizers help many women',
        'Low-dose vaginal estrogen is very effective and safe',
        'Communication with your partner is essential',
        'Intimacy can remain fulfilling with some adaptations'
      ]
    }
  },

  'menopause-work': {
    id: 'menopause-work',
    title: 'Menopause at Work',
    subtitle: 'Managing symptoms professionally',
    readTime: '6 min read',
    category: 'lifestyle',
    content: {
      intro: `Managing menopause symptoms while maintaining professional performance can be challenging. Hot flashes in meetings, brain fog during presentations, and fatigue affecting productivity are real issues. Here's how to navigate menopause in the workplace.`,
      sections: [
        {
          title: 'Common Workplace Challenges',
          content: `**Physical Symptoms at Work:**
• Hot flashes during meetings or presentations
• Night sweats causing fatigue next day
• Needing bathroom breaks more frequently
• Discomfort in warm offices

**Cognitive Symptoms:**
• Difficulty concentrating
• Memory lapses
• Word-finding problems
• Reduced confidence

**Emotional Impact:**
• Anxiety about symptoms being noticed
• Irritability with colleagues
• Feeling less capable
• Isolation from peers

**Statistics:** Up to 25% of women consider leaving work due to menopause symptoms.`
        },
        {
          title: 'Practical Coping Strategies',
          content: `**For Hot Flashes:**
• Portable fan at desk
• Layered clothing
• Cold water always available
• Cooling products (wipes, neck wraps)
• Sit near windows or fans when possible

**For Cognitive Symptoms:**
• Write everything down
• Use calendar reminders religiously
• Allow extra prep time
• Record meetings (if allowed)
• Don't over-schedule

**For Fatigue:**
• Tackle important tasks when energy is highest
• Take short breaks
• Brief walks if possible
• Avoid heavy lunches
• Strategic caffeine use (not too late)`
        },
        {
          title: 'Workplace Modifications',
          content: `**Physical Environment:**
• Request desk near window or temperature control
• Access to fan or portable AC
• Easy bathroom access
• Quiet space for breaks if needed

**Schedule Flexibility:**
• Earlier or later start times
• Working from home options
• Flexibility for appointments
• Breaks when needed

**Workload:**
• Realistic deadlines
• Support during difficult days
• Understanding for occasional lower productivity

**Many Countries Now Recognize:** Menopause may qualify for workplace accommodations under disability or equality laws.`
        },
        {
          title: 'To Tell or Not to Tell',
          content: `**Potential Benefits of Disclosure:**
• Explain any changes in performance
• Request needed accommodations
• Find support from others
• Reduce stress of hiding symptoms
• Normalize the conversation

**Potential Concerns:**
• Fear of being seen as less capable
• Ageism and sexism
• Career impact
• Uncomfortable conversations

**If You Choose to Share:**
• Decide who needs to know
• Focus on solutions, not complaints
• Frame as temporary transition
• Know your rights
• Have specific accommodation requests ready

**You're Not Obligated:** It's a personal decision based on your workplace culture and comfort.`
        },
        {
          title: 'Supporting Yourself',
          content: `**Build Your Support System:**
• Find allies at work
• Connect with others going through menopause
• Seek mentors who've navigated this
• Consider employee resource groups

**Professional Help:**
• Manage symptoms effectively with healthcare provider
• Consider whether current treatments are working
• Time treatments for work (some take at night)

**Career Perspective:**
• This phase is temporary
• Your experience and skills remain valuable
• Many successful women have navigated this
• Don't make major career decisions during rough patches

**Self-Advocacy:**
• Know your worth
• Document your contributions
• Don't apologize for biological processes
• Seek support if facing discrimination`
        }
      ],
      keyTakeaways: [
        'Practical strategies can help manage symptoms at work',
        'Workplace accommodations may be available',
        'Disclosure is a personal choice',
        'You\'re not alone - millions of women navigate this',
        'This phase is temporary; your value remains'
      ]
    }
  },

  'hrt-explained': {
    id: 'hrt-explained',
    title: 'Hormone Therapy Explained',
    subtitle: 'Benefits, risks, types, and who it\'s for',
    readTime: '10 min read',
    category: 'treatment',
    content: {
      intro: `Hormone therapy (HT), also called hormone replacement therapy (HRT), is the most effective treatment for menopausal symptoms. However, it's not right for everyone. Understanding the facts helps you make an informed decision with your healthcare provider.`,
      sections: [
        {
          title: 'What is Hormone Therapy?',
          content: `Hormone therapy replaces hormones your body no longer makes in sufficient quantities.

**Types:**
• **Estrogen-only therapy (ET):** For women who've had a hysterectomy
• **Combined therapy (EPT):** Estrogen plus progestogen for women with a uterus (protects against uterine cancer)

**Delivery Methods:**
• Pills (oral)
• Patches (transdermal)
• Gels and sprays
• Vaginal rings, creams, tablets
• Implants

**Doses:**
• Standard dose
• Low dose
• Ultra-low dose

Transdermal (patch, gel) may be safer regarding blood clot risk.`
        },
        {
          title: 'Benefits',
          content: `**Symptom Relief:**
• Reduces hot flashes by 75-90%
• Improves sleep
• Relieves vaginal dryness
• Improves mood and anxiety
• Reduces joint aches
• May help with brain fog

**Protective Effects:**
• Prevents bone loss and reduces fracture risk
• May protect against colon cancer
• May reduce diabetes risk
• When started early, may have cardiovascular benefits

**Quality of Life:**
Many women report significant improvement in overall wellbeing.`
        },
        {
          title: 'Risks and Concerns',
          content: `**Potential Risks (vary by type, dose, timing):**

**Blood Clots:**
• Slightly increased risk with oral estrogen
• Transdermal delivery much lower risk
• Higher risk with obesity, smoking, immobility

**Breast Cancer:**
• Combined therapy: slightly increased risk after 5+ years
• Estrogen-only: no increased risk (possibly protective)
• Risk comparable to other factors (alcohol, obesity)

**Stroke:**
• Slight increase, mainly with oral estrogen
• Transdermal lower risk

**Note:** For most healthy women under 60, or within 10 years of menopause, benefits generally outweigh risks.`
        },
        {
          title: 'Who Should Consider HT?',
          content: `**Good Candidates:**
• Moderate to severe hot flashes/night sweats
• Significant vaginal symptoms
• Under 60 or within 10 years of menopause
• At risk for osteoporosis
• Premature or early menopause

**Who Should Avoid:**
• History of breast cancer
• History of blood clots or stroke
• Active liver disease
• Unexplained vaginal bleeding
• History of heart attack

**Requires Careful Discussion:**
• Family history of breast cancer
• Migraine with aura
• Gallbladder disease
• Elevated triglycerides

**Important:** Risks are generally lower when started closer to menopause onset ("timing hypothesis").`
        },
        {
          title: 'Making the Decision',
          content: `**Questions to Discuss with Your Doctor:**
• How severe are my symptoms?
• What are my personal risk factors?
• What type and dose makes sense for me?
• How long should I use it?
• What's my exit plan?

**Individualized Approach:**
• Lowest effective dose
• Shortest duration needed
• Regular reassessment
• Appropriate type for your risk profile

**If You Start HT:**
• Annual check-ups
• Report new symptoms
• Reassess periodically
• Don't stop abruptly (taper)

**Alternatives Exist:**
If HT isn't right for you, other treatments can help (see Natural Remedies section).`
        }
      ],
      keyTakeaways: [
        'HT is the most effective treatment for hot flashes',
        'Transdermal delivery (patches) has lower blood clot risk',
        'Benefits usually outweigh risks for healthy women under 60',
        'Decision should be individualized with your doctor',
        'Regular reassessment is important'
      ]
    }
  },

  'natural-remedies': {
    id: 'natural-remedies',
    title: 'Natural Remedies & Alternatives',
    subtitle: 'Herbal supplements and alternative therapies',
    readTime: '7 min read',
    category: 'treatment',
    content: {
      intro: `Not everyone can or wants to use hormone therapy. Various natural remedies and alternative approaches may help manage symptoms. While evidence varies, some women find relief with these options.`,
      sections: [
        {
          title: 'Herbal Supplements',
          content: `**Black Cohosh:**
• Most studied herb for menopause
• May reduce hot flashes
• Generally safe short-term
• Avoid with liver problems

**Red Clover:**
• Contains isoflavones (phytoestrogens)
• Mixed evidence for hot flashes
• Generally safe

**Evening Primrose Oil:**
• Sometimes used for hot flashes
• Limited evidence
• May help with breast tenderness

**St. John's Wort:**
• For mild depression
• Interacts with many medications
• Don't combine with antidepressants

**Valerian:**
• May help with sleep
• Generally safe short-term

**Important:** "Natural" doesn't mean safe. Supplements can interact with medications and have side effects. Discuss with your doctor before starting.`
        },
        {
          title: 'Phytoestrogens',
          content: `Plant compounds that weakly mimic estrogen:

**Soy Isoflavones:**
• Most evidence among phytoestrogens
• May modestly reduce hot flashes
• Safe in food form
• Supplement evidence mixed

**Flaxseed:**
• Contains lignans (phytoestrogens)
• Ground flax absorbs better
• Also provides fiber and omega-3s

**Other Sources:**
• Tempeh, miso, tofu
• Chickpeas, lentils
• Some whole grains

**Effectiveness:**
• Effects modest compared to HRT
• May take 4-6 weeks to notice
• Work better for some women than others
• Dietary sources preferred over supplements`
        },
        {
          title: 'Mind-Body Practices',
          content: `**Cognitive Behavioral Therapy (CBT):**
• Strong evidence for hot flashes and mood
• Changes how you respond to symptoms
• Long-lasting effects
• Available in person or online

**Mindfulness/Meditation:**
• Reduces stress response
• May decrease hot flash distress
• Improves sleep
• Benefits mood

**Yoga:**
• Combines movement and mindfulness
• May reduce symptoms
• Improves flexibility and strength
• Social support aspect

**Hypnotherapy:**
• Clinical hypnosis for hot flashes
• Evidence suggests significant reduction
• Find trained practitioner

**Acupuncture:**
• Some evidence for hot flashes
• May help with sleep and mood
• Generally safe with qualified practitioner`
        },
        {
          title: 'Non-Hormonal Medications',
          content: `Prescription options for those who can't use hormones:

**For Hot Flashes:**
• Low-dose antidepressants (SSRIs, SNRIs)
• Gabapentin
• Clonidine
• Oxybutynin
• Fezolinetant (newest option)

**For Mood:**
• Antidepressants
• Anti-anxiety medications

**For Sleep:**
• Sleep aids (short-term)
• Low-dose antidepressants

**For Vaginal Symptoms:**
• Ospemifene (oral, non-estrogen)
• Vaginal DHEA (Intrarosa)
• Moisturizers and lubricants

Discuss with your doctor which might work for you.`
        },
        {
          title: 'What to Know Before Trying',
          content: `**Quality Concerns:**
• Supplements aren't regulated like drugs
• Potency and purity vary
• Look for third-party testing (USP, NSF)

**Safety:**
• Can interact with medications
• May not be safe with certain conditions
• "Natural" doesn't mean risk-free
• Tell your doctor what you're taking

**Setting Expectations:**
• Effects usually milder than HRT
• May take weeks to notice
• Individual responses vary
• Multiple approaches may help

**Red Flags:**
• Products claiming to "cure" menopause
• Extreme claims
• Pressure to buy
• Very cheap products

**Best Approach:** Combine evidence-based natural approaches with lifestyle changes. Discuss all supplements with your healthcare provider.`
        }
      ],
      keyTakeaways: [
        'Evidence varies for natural remedies',
        'CBT has strong evidence for symptom management',
        'Phytoestrogens may help modestly',
        '"Natural" doesn\'t mean safe - discuss with your doctor',
        'Non-hormonal prescription options also exist'
      ]
    }
  },

  'when-see-doctor': {
    id: 'when-see-doctor',
    title: 'When to See a Doctor',
    subtitle: 'Red flags and specialist referrals',
    readTime: '5 min read',
    category: 'treatment',
    content: {
      intro: `While menopause is natural, some symptoms require medical attention. Knowing when to seek help ensures you get appropriate care and rules out other conditions that might mimic menopause.`,
      sections: [
        {
          title: 'Routine Menopause Care',
          content: `**Schedule an Appointment For:**
• Discussion of menopause management options
• Persistent symptoms affecting quality of life
• Questions about hormone therapy
• Bone health assessment (if at risk)
• Annual wellness exams

**What to Discuss:**
• All symptoms you're experiencing
• How symptoms affect daily life
• Family medical history
• Current medications and supplements
• Your preferences for treatment

**Preparing for Your Visit:**
• Track symptoms (type, frequency, severity)
• List questions in advance
• Bring medication list
• Know your family history`
        },
        {
          title: 'Urgent Symptoms (See Doctor Promptly)',
          content: `**Abnormal Bleeding:**
• Bleeding after 12 months without period
• Very heavy bleeding
• Bleeding between periods
• Bleeding after sex

**Severe Symptoms:**
• Hot flashes severely disrupting life
• Sleep deprivation affecting functioning
• Severe mood symptoms
• Symptoms not responding to treatment

**Pain:**
• Painful intercourse not improving with treatment
• Pelvic pain
• Severe headaches

These may indicate menopause-related issues or other conditions that need evaluation.`
        },
        {
          title: 'Red Flags (Seek Immediate Care)',
          content: `**Call Your Doctor or Seek Emergency Care If You Have:**
• Chest pain or pressure
• Difficulty breathing
• Signs of blood clot (leg swelling, pain, warmth)
• Sudden severe headache
• Vision changes
• Thoughts of harming yourself

**Other Concerning Symptoms:**
• Unexplained weight loss
• Night sweats with fever
• Severe abdominal pain
• New lumps or masses

**Note:** These symptoms may not be menopause-related and need prompt evaluation.`
        },
        {
          title: 'Specialist Referrals',
          content: `**Menopause Specialist:**
• Complex symptom management
• HRT concerns or questions
• Failed previous treatments
• Certified through NAMS (North American Menopause Society)

**Gynecologist:**
• Vaginal/urinary symptoms
• Abnormal bleeding
• Pelvic issues

**Endocrinologist:**
• Thyroid concerns
• Premature menopause
• Complex hormonal issues

**Mental Health Professional:**
• Depression or anxiety
• Significant mood changes
• Relationship issues

**Other Specialists:**
• Cardiologist (heart concerns)
• Rheumatologist (joint issues)
• Dermatologist (skin changes)
• Sleep specialist (sleep disorders)`
        },
        {
          title: 'Advocating for Yourself',
          content: `**If You Feel Dismissed:**
• Menopause symptoms are real and valid
• Seek a second opinion if needed
• Find a provider who listens
• Look for menopause-certified practitioners

**Your Rights:**
• To be taken seriously
• To understand your options
• To participate in decisions
• To decline treatments
• To seek other opinions

**Resources:**
• NAMS Menopause Practitioner Directory
• Women's health clinics
• Teaching hospitals
• Online telehealth options

**Remember:** You know your body. If something feels wrong, persist until you get answers.`
        }
      ],
      keyTakeaways: [
        'Routine care helps manage the transition',
        'Abnormal bleeding always needs evaluation',
        'Severe symptoms affecting daily life warrant medical attention',
        'Specialists exist for complex cases',
        'Advocate for yourself if you feel dismissed'
      ]
    }
  }
};

export default educationTopics;
